import { Icon } from '@ps/isp-web-component-library';
import { CloseMedium } from '@ps/isp-web-component-library/icons/CloseMedium';
import { FeatureFlag, Size } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import type { Reference } from '@ps/web-martech-cms-types';
import { useScreenSize } from '@ps/web-shared-components';
import type { SportsReference } from '@ps/web-sports-page-types';
import { useLayout } from '@ps/web-sports-utils';
import { usePreventScroll } from '@react-aria/overlays';
import classNames from 'clsx';
import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { CMSReferences } from '@swf/cms';
import useMutationObserver from '@swf/core/hooks/useMutationObserver';
import { select } from '@swf/core/store';
import { getKeyboardFocusableElements } from '@swf/core/utils';

import { TXTWCL_Close } from './Navigation.messages.json';
import styles from './Navigation.scss';

interface Props {
  navigationSection: Reference[] | SportsReference[];
}

const mutationObserverHookOptions = {
  attributes: true,
  childList: true,
  subtree: true,
  triggerBeforeMutation: true
};

const Navigation: React.FC<Props> = ({ navigationSection }) => {
  const { greaterThan, screenSize } = useScreenSize();
  const { showNavigationSection, setShowNavigationSection } = useLayout();
  const isMobileStickyEnabled = useFeature(FeatureFlag.ISPEnableMobileStickyQuickLinks);
  const isMobile = useSelector(select.device.isMobile);
  const { formatMessage } = useIntl();
  const overlayRef = useRef<HTMLDivElement>(null);
  const navigationSectionContentRef = useRef<HTMLDivElement>(null);
  const navigationSectionFirstFocusableElement = useRef<Element>(undefined);
  const navigationSectionLastFocusableElement = useRef<Element>(undefined);

  const onNavigationSectionContentChange = useCallback(() => {
    if (navigationSectionContentRef.current) {
      const allNavigationSectionFocusableElements = getKeyboardFocusableElements(
        navigationSectionContentRef.current
      );
      const [firstElement] = allNavigationSectionFocusableElements;
      navigationSectionFirstFocusableElement.current = firstElement;
      navigationSectionLastFocusableElement.current =
        allNavigationSectionFocusableElements[allNavigationSectionFocusableElements.length - 1];
    }
  }, [navigationSectionContentRef]);

  const closeNavigationSection = (event: React.MouseEvent) => {
    if (event.target === overlayRef.current) {
      setShowNavigationSection(false);
    }
  };

  usePreventScroll({ isDisabled: !showNavigationSection });

  useEffect(() => {
    if (showNavigationSection && greaterThan(Size.Large)) {
      setShowNavigationSection(false);
    }
  }, [greaterThan, screenSize, setShowNavigationSection, showNavigationSection]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && showNavigationSection) {
        if (
          !event.shiftKey &&
          navigationSectionLastFocusableElement.current === document.activeElement
        ) {
          setShowNavigationSection(false);
        }
        if (
          event.shiftKey &&
          navigationSectionFirstFocusableElement.current === document.activeElement
        ) {
          setShowNavigationSection(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setShowNavigationSection, showNavigationSection]);

  useMutationObserver(navigationSectionContentRef, onNavigationSectionContentChange, {
    ...mutationObserverHookOptions,
    childList: false
  });

  if (isMobileStickyEnabled) {
    return (
      <>
        <div
          className={classNames(styles.fullscreenNavigation, {
            [styles.fullscreenNavigationOpen]: showNavigationSection
          })}
        >
          <div className={styles.fullscreenNavigationCloseHeader}>
            <button
              className={styles.fullscreenNavigationCloseHeaderButton}
              data-test-id="navigation-close-button"
              type="button"
              onClick={() => setShowNavigationSection(false)}
              aria-label={formatMessage(TXTWCL_Close)}
            >
              <Icon size="lg" aria-hidden icon={CloseMedium} />
            </button>
          </div>

          <div className={styles.fullscreenNavigationContent} ref={navigationSectionContentRef}>
            <CMSReferences references={navigationSection} />
          </div>
        </div>
        <div
          className={classNames(styles.outsideNavigationOverlay, {
            [styles.outsideNavigationOverlayOpen]: showNavigationSection
          })}
          onClick={closeNavigationSection}
          ref={overlayRef}
        />
      </>
    );
  }

  return (
    <div
      className={classNames(styles.navigation, {
        [styles.navigationOpen]: showNavigationSection,
        [styles.mobile]: isMobile
      })}
      onClick={closeNavigationSection}
      ref={overlayRef}
    >
      <div className={styles.navigationContent} ref={navigationSectionContentRef}>
        <CMSReferences references={navigationSection} />
      </div>
    </div>
  );
};

export default Navigation;
